import { Outlet, useNavigate } from 'react-router-dom'
import { memo, Suspense, useEffect } from 'react'
import {
    getDatabase,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    onValue,
    ref,
    Unsubscribe,
} from 'firebase/database'
import {
    BankDetails,
    TeacherFields,
    TeacherNotifications,
    toIdentificationDocument,
    AccreditationDocument,
    toAccreditationDocument,
} from '../../redux/types'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
    setDataLoaded,
    setEmploymentSetting,
    setRegFields,
    setRejectedState,
    setTeacherLocationState,
    setTeacherNotifications,
    // setTeacherProfile,
    // setEducatorProfile,
    // setSupportStaffProfile,
} from '../../redux/userRegistrationSlice'
import { Stack } from '@mui/material'
import HomeAddressModal from './components/HomeAddressModal'
import HowDidYouHear from '../../components/Popup/HowDidYouHear'
import { FieldState, TeacherAccountState } from '../../firebase/types-teacher'
import { stateConversion, whichEmployentSetting } from './helper'
import { RootState } from '../../redux/store'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment-timezone'
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner'
import EducatorLevelModal from './components/EducatorLevelModal'
import {
    selectUserAccountTypeCheck,
    selectUserUid,
    // setAccountState,
} from '../../redux/userSlice'
import {
    removeAccreditationDoc,
    setAccreditationSnapshot,
    setAccountState,
    setEmploymentSettings,
    setLoading,
    setSetLocation,
    upsertAccreditationDoc,
    setData,
    selectDerivedIncompleteTeacherEmploymentSetting,
} from '../../redux/teacherSlice'
import dayjs from 'dayjs'
import ESQualificationModal from './components/ESQualificationModal'
import { useAccountTypeCheck } from './components/AccountTypeCheck'
import TeacherHeader from '../../components/Header/TeacherHeader'
import { TabNames } from '../../routers/type'
import { logger } from '../../helpers/logger'
import { db } from '../../firebase/configs'
import TeacherOnboardingComponent from './teacher/onboarding/TeacherOnboardingComponent'
import RegistrationModals_v2 from './RegistrationModals/RegistrationModals_v2'
import useTeacherDataListener from './teacher/hooks/useTeacherDataListener'

const TeacherDashboard = () => {
    const loading = useAccountTypeCheck('teacher')

    if (loading)
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="teacher-dashboard-loading"
            />
        )
    return (
        <>
            <Stack
                sx={{ height: '100%', minHeight: '100vh', overflow: 'hidden' }}
            >
                <RegistrationModals_v2 />

                <TeacherListeners />
                {/* Employment Type based listners */}
                <CrtListeners />
                <SupportStaffListeners />
                <EducatorListeners />

                <TeacherUtils />
                <TeacherOnboardingComponent />
                <HowDidYouHear />
                <HomeAddressModal />
                <EducatorLevelModal />
                <ESQualificationModal />
                <TeacherHeader />
                <Suspense
                    fallback={
                        <LoadingSpinner fillViewPortHeight fillViewPortWidth />
                    }
                >
                    <Stack
                        style={{ display: 'flex', flex: 1 }}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Outlet />
                    </Stack>
                </Suspense>
            </Stack>
        </>
    )
}

const TeacherUtils = memo(() => {
    const userId = useSelector(
        (state: RootState) => state.user.auth.status?.uid
    )

    const dispatch = useDispatch()

    const setLocation = useSelector(
        (state: RootState) => state.userReg.fields.setLocation
    )
    useEffect(() => {
        // For now, set location to setLocation, until there is a need to be able to change this.
        dispatch(setTeacherLocationState(setLocation?.homeState))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setLocation?.homeState])

    const employmentSetting = useSelector(
        (state: RootState) => state.userReg.fields.employmentSetting
    )
    const locationState = useSelector(
        (state: RootState) => state.userReg.locationState
    )

    useEffect(() => {
        if (locationState) {
            dispatch(
                setEmploymentSetting(
                    whichEmployentSetting(locationState, employmentSetting)
                )
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employmentSetting, locationState])

    const accountType = useSelector(selectUserAccountTypeCheck)
    const navigate = useNavigate()
    // Employment setting listener
    useEffect(() => {
        if (accountType === undefined) return
        let unsub: Unsubscribe
        if (accountType === 'teacher') {
            // Listen to employment setting, if not defined need to prompt user to select
            const empSettingRef = ref(
                getDatabase(),
                `teachers/${userId}/employmentSetting`
            )
            unsub = onValue(empSettingRef, (snap) => {
                if (!snap.exists()) {
                    navigate(`/${TabNames.ON_BOARDING}`, {
                        replace: true,
                        state: {
                            employmentSettingMissing: true,
                        },
                    })
                }
                // dispatch(setEmploymentSettingExists(snap.exists()))
            })
        }
        return () => unsub && unsub()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountType, navigate])
    return null
})

const TeacherListeners = memo(() => {
    const userId = useSelector(selectUserUid)

    const dispatch = useDispatch()

    // Account state listener
    useEffect(() => {
        if (!userId) return

        dispatch(setLoading('accountState'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/accountState/value`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(setAccountState(snap.val()))
                } else {
                    dispatch(setAccountState(null))
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setAccountState(undefined))
        }
    }, [userId, dispatch])

    // Set location listener
    useEffect(() => {
        if (!userId) return
        dispatch(setLoading('setLocation'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/setLocation`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(setSetLocation(snap.val()))
                } else {
                    dispatch(setSetLocation(null))
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setSetLocation(undefined))
        }
    }, [userId, dispatch])

    // Employment Setting listener
    useEffect(() => {
        if (!userId) return

        dispatch(setLoading('employmentSettings'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/employmentSetting`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(setEmploymentSettings(snap.val()))
                } else {
                    dispatch(setEmploymentSettings(null))
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setEmploymentSettings(undefined))
        }
    }, [userId, dispatch])

    // Accreditation listener
    useEffect(() => {
        if (!userId) return

        dispatch(setLoading('accreditation'))
        const accreditationRef = ref(db, `teachers/${userId}/accreditation`)

        const initialUnsub = onValue(
            accreditationRef,
            (snap) => {
                dispatch(setAccreditationSnapshot(snap.val() ?? null))
            },
            { onlyOnce: true }
        )

        const addedUnsub = onChildAdded(accreditationRef, (snap) => {
            const key = snap.key
            if (!key) return
            dispatch(upsertAccreditationDoc({ id: key, doc: snap.val() }))
        })

        const changedUnsub = onChildChanged(accreditationRef, (snap) => {
            const key = snap.key
            if (!key) return
            dispatch(upsertAccreditationDoc({ id: key, doc: snap.val() }))
        })

        const removedUnsub = onChildRemoved(accreditationRef, (snap) => {
            const key = snap.key
            if (!key) return
            dispatch(removeAccreditationDoc(key))
        })

        return () => {
            initialUnsub()
            addedUnsub()
            changedUnsub()
            removedUnsub()
            dispatch(setAccreditationSnapshot(undefined))
        }
    }, [userId, dispatch])

    // Preferred languages listeners
    useTeacherDataListener('languages')

    // On the dashboard, listen to auth.currentUser registration data.
    useEffect(() => {
        let unsub: Unsubscribe | undefined = undefined
        if (userId) {
            const teacherRef = ref(getDatabase(), `teachers/${userId}`)
            logger.debug('ATTACH-LISTENER: Teacher Data')
            unsub = onValue(teacherRef, (snapshot) => {
                if (snapshot.exists()) {
                    const teacherData = snapshot.val()

                    // Dispatch data
                    batch(() => {
                        dispatch(
                            setData({
                                key: 'names',
                                value: {
                                    firstName: teacherData.firstName,
                                    lastName: teacherData.lastName,
                                    firstNameActual:
                                        teacherData.firstNameActual,
                                },
                            })
                        )

                        dispatch(
                            setData({
                                key: 'contact',
                                value: {
                                    email: teacherData.email,
                                    contact: teacherData.contact,
                                    homeLocation: teacherData.homeLocation,
                                },
                            })
                        )
                        dispatch(
                            setData({
                                key: 'profilePicture',
                                value: teacherData.profilePicture,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'identity',
                                value: teacherData.identity,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'workingRights',
                                value: teacherData.workingRights,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'reference',
                                value: teacherData.reference,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'type',
                                value: teacherData.type,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'emergencyContacts',
                                value: {
                                    primary: teacherData.firstEmergencyContact,
                                    secondary:
                                        teacherData.secondEmergencyContact,
                                },
                            })
                        )
                        dispatch(
                            setData({
                                key: 'service',
                                value: teacherData.service,
                            })
                        )
                        dispatch(
                            setData({
                                key: 'workingHistory',
                                value: teacherData.workingHistory,
                            })
                        )

                        dispatch(
                            setData({
                                key: 'dateApproved',
                                value: teacherData.accountState.dateApproved,
                            })
                        )
                    })

                    const teacherAccountState = (teacherData.accountState
                        ?.value ||
                        TeacherAccountState.INCOMPLETE) as TeacherAccountState

                    dispatch(setAccountState(teacherAccountState))

                    // If user is rejected, check if applyDate is set (disabled certain features)
                    rejectedCheck: if (
                        teacherAccountState === TeacherAccountState.REJECTED
                    ) {
                        if (!teacherData.accountState.applyDate) {
                            dispatch(setRejectedState(null))
                            break rejectedCheck
                        }
                        const readyDate = dayjs(
                            teacherData.accountState.applyDate,
                            'DD-MM-YYYY'
                        ).add(6, 'months')

                        if (readyDate.isBefore(dayjs())) {
                            dispatch(setRejectedState(false))
                        } else {
                            dispatch(setRejectedState(readyDate))
                        }
                    } else {
                        dispatch(setRejectedState(null))
                    }

                    const isPostReg = [
                        TeacherAccountState.APPROVED,
                        TeacherAccountState.ONHOLD,
                    ].includes(teacherAccountState)

                    let teacherFields: TeacherFields = {
                        ...(teacherData.accountState
                            ? {
                                  accountState: {
                                      ...teacherData.accountState,
                                      value: teacherData.accountState?.value
                                          ? teacherData.accountState.value
                                          : 'incomplete',
                                  },
                              }
                            : {
                                  accountState: {
                                      value: 'incomplete',
                                      firstTime: false,
                                  },
                              }),
                        ...(!isPostReg
                            ? {
                                  workingRights: {
                                      ...teacherData.workingRights,
                                      state: stateConversion(
                                          teacherData.workingRights?.state,
                                          teacherAccountState
                                      ),
                                  },
                                  reference: {
                                      ...teacherData.reference,
                                      state: stateConversion(
                                          teacherData.reference?.state,
                                          teacherAccountState
                                      ),
                                  },
                                  ...(teacherData.employmentDeclarations
                                      ? {
                                            employmentDeclarations:
                                                teacherData.employmentDeclarations,
                                        }
                                      : {
                                            employmentDeclarations: {
                                                VIC: { state: 'incomplete' },
                                            },
                                        }),
                              }
                            : null),
                        ...(teacherData.firstNameActual
                            ? {
                                  firstName: {
                                      ...teacherData.firstNameActual,
                                      state: stateConversion(
                                          teacherData.firstNameActual?.state ||
                                              teacherData.firstNameActual?.value
                                              ? FieldState.SUBMITTED
                                              : FieldState.INCOMPLETE,
                                          teacherAccountState
                                      ),
                                  },
                                  preferredName: {
                                      ...teacherData.firstName,
                                      state: stateConversion(
                                          teacherData.firstName?.state,
                                          teacherAccountState
                                      ),
                                  },
                              }
                            : {
                                  firstName: {
                                      ...teacherData.firstName,
                                      state: stateConversion(
                                          teacherData.firstName?.state ||
                                              teacherData.firstName?.value
                                              ? FieldState.SUBMITTED
                                              : FieldState.INCOMPLETE,
                                          teacherAccountState
                                      ),
                                  },
                              }),
                        lastName: {
                            ...teacherData.lastName,
                            state: stateConversion(
                                teacherData.lastName?.state ||
                                    teacherData.lastName?.value
                                    ? FieldState.SUBMITTED
                                    : FieldState.INCOMPLETE,
                                teacherAccountState
                            ),
                        },
                        email: {
                            ...teacherData.email,
                            state: stateConversion(
                                teacherData.email?.state,
                                teacherAccountState
                            ),
                        },
                        contact: {
                            ...teacherData.contact,
                            state: stateConversion(
                                teacherData.contact?.state,
                                teacherAccountState
                            ),
                        },
                        homeAddress: {
                            ...teacherData.homeLocation,
                            state: stateConversion(
                                teacherData.homeLocation?.state,
                                teacherAccountState
                            ),
                        },
                        setLocation: {
                            ...teacherData.setLocation,
                            state: stateConversion(
                                teacherData.setLocation?.state,
                                teacherAccountState
                            ),
                        },
                        profilePicture: {
                            ...teacherData.profilePicture,
                            state: stateConversion(
                                teacherData.profilePicture?.state,
                                teacherAccountState
                            ),
                        },
                        firstEmergencyContact: {
                            ...teacherData.firstEmergencyContact,
                            state: stateConversion(
                                teacherData.firstEmergencyContact?.state,
                                teacherAccountState
                            ),
                        },
                        secondEmergencyContact: {
                            ...teacherData.secondEmergencyContact,
                            state: stateConversion(
                                teacherData.secondEmergencyContact?.state,
                                teacherAccountState
                            ),
                        },

                        // Profile items
                        // profile: teacherData.profile || null,
                        // favouriteSubjects:
                        //     teacherData.favouriteSubjects || null,
                        // years: teacherData.years || null,
                        type: teacherData.type || null,

                        ...(teacherData.completionRate
                            ? { completionRate: teacherData.completionRate }
                            : undefined),
                        ...(teacherData.uiState
                            ? { uiState: teacherData.uiState }
                            : undefined),
                        ...(teacherData.howDidYouHear
                            ? { howDidYouHear: teacherData.howDidYouHear }
                            : null),
                    }

                    const userLocation =
                        teacherFields.setLocation?.homeState ||
                        teacherFields.homeAddress?.homeState ||
                        'VIC'
                    // Set employment setting (default to teacher at set location => home location => VIC)
                    // teacherFields.employmentSetting = teacherData.employmentSetting ? teacherData.employmentSetting : { [`${uuidv4()}`]: { locationState: userLocation, type: 'school' } }
                    teacherFields.employmentSetting =
                        teacherData.employmentSetting
                    // Only seed a default setting if there are no employment settings at all.
                    // Do not auto-add for a missing current location, otherwise user-removed
                    // settings can be unintentionally recreated.
                    if (
                        Object.keys(teacherFields.employmentSetting || {})
                            .length === 0
                    ) {
                        if (!teacherFields.employmentSetting)
                            teacherFields.employmentSetting = {}
                        teacherFields.employmentSetting[`${uuidv4()}`] = {
                            locationState: userLocation,
                            type: 'school',
                        }
                    }

                    // const empSetting = whichEmployentSetting(
                    //     userLocation,
                    //     teacherFields.employmentSetting
                    // )

                    // // Get accreditation document details for Teacher and/or Educator
                    // let educatorDetails: {
                    //     [locationState: string]:
                    //         | AccreditationDocumentDetails[]
                    //         | null
                    // } = {}
                    // let schoolsDetails: {
                    //     [locationState: string]:
                    //         | AccreditationDocumentDetails[]
                    //         | null
                    // } = {}
                    // let supportStaffDetails: {
                    //     [locationState: string]:
                    //         | AccreditationDocumentDetails[]
                    //         | null
                    // } = {}

                    // let educatorDocs: {
                    //     [locationState: string]: RegAccredDocs | null
                    // } = {}
                    // let schoolsDocs: {
                    //     [locationState: string]: RegAccredDocs | null
                    // } = {}
                    // let supportStaffDocs: {
                    //     [locationState: string]: RegAccredDocs | null
                    // } = {}

                    // // teacherFields.accreditation = { earlyChildcare: null, school: null }
                    // teacherFields.accreditation = {}
                    // teacherFields.accreditation.earlyChildcare = null
                    // teacherFields.accreditation.school = null
                    // teacherFields.accreditation.supportStaff = null

                    // // Process accreditation of user's location
                    // // Get set location accred docs + details
                    // educatorDetails[`${userLocation}`] =
                    //     getEducatorAccreditations(empSetting)
                    // schoolsDetails[`${userLocation}`] =
                    //     getTeacherAccreditations(userLocation)
                    // supportStaffDetails[`${userLocation}`] =
                    //     getSupportStaffAccreditations(
                    //         empSetting,
                    //         teacherAccountState
                    //     )

                    // // Count expired mandatory accreditations while processing.
                    // let mandatoryRequiredCount = 0

                    // let [educatorAccreds, educatorExpiredCount] =
                    //     getAccreditationDocuments(
                    //         teacherData.accreditation || {},
                    //         educatorDetails[`${userLocation}`],
                    //         teacherAccountState
                    //     )
                    // educatorDocs[`${userLocation}`] = educatorAccreds

                    // let [schoolAccreds, schoolExpiredCount] =
                    //     getAccreditationDocuments(
                    //         teacherData.accreditation || {},
                    //         schoolsDetails[`${userLocation}`],
                    //         teacherAccountState
                    //     )
                    // schoolsDocs[`${userLocation}`] = schoolAccreds

                    // let [supportStaffAccreds, supportStaffExpiredCount] =
                    //     getAccreditationDocuments(
                    //         teacherData.accreditation || {},
                    //         supportStaffDetails[`${userLocation}`],
                    //         teacherAccountState
                    //     )
                    // supportStaffDocs[`${userLocation}`] = supportStaffAccreds

                    // // Process accreditations for other employment setting locations.
                    // for (let el of Object.values(
                    //     teacherFields.employmentSetting || {}
                    // )) {
                    //     // Get Accreditation requirement details
                    //     if (el.type === 'earlyChildcare') {
                    //         if (!(el.locationState in educatorDetails)) {
                    //             logger.debug(
                    //                 `adding ${el.locationState} details for educators. level: ${el.level}`
                    //             )
                    //             educatorDetails[`${el.locationState}`] =
                    //                 getEducatorAccreditations(el)
                    //         } else {
                    //             mandatoryRequiredCount += educatorExpiredCount
                    //         }

                    //         if (!(el.locationState in educatorDocs)) {
                    //             let [accredDocs, expiredCount] =
                    //                 getAccreditationDocuments(
                    //                     teacherData.accreditation || {},
                    //                     educatorDetails[`${el.locationState}`],
                    //                     teacherAccountState
                    //                 )
                    //             educatorDocs[`${el.locationState}`] =
                    //                 accredDocs || {}
                    //             mandatoryRequiredCount += expiredCount
                    //         }
                    //     }

                    //     if (el.type === 'school') {
                    //         if (!(el.locationState in schoolsDetails)) {
                    //             schoolsDetails[`${el.locationState}`] =
                    //                 getTeacherAccreditations(el.locationState)
                    //         } else {
                    //             mandatoryRequiredCount += schoolExpiredCount
                    //         }

                    //         if (!(el.locationState in schoolsDocs)) {
                    //             let [accredDocs, expiredCount] =
                    //                 getAccreditationDocuments(
                    //                     teacherData.accreditation || {},
                    //                     schoolsDetails[`${el.locationState}`],
                    //                     teacherAccountState
                    //                 )
                    //             schoolsDocs[`${el.locationState}`] =
                    //                 accredDocs || {}
                    //             mandatoryRequiredCount += expiredCount
                    //         }
                    //     }

                    //     if (el.type === 'supportStaff') {
                    //         if (!(el.locationState in supportStaffDetails)) {
                    //             supportStaffDetails[`${el.locationState}`] =
                    //                 getSupportStaffAccreditations(
                    //                     el,
                    //                     teacherAccountState
                    //                 )
                    //         } else {
                    //             mandatoryRequiredCount +=
                    //                 supportStaffExpiredCount
                    //         }

                    //         if (!(el.locationState in supportStaffDetails)) {
                    //             let [accredDocs, expiredCount] =
                    //                 getAccreditationDocuments(
                    //                     teacherData.accreditation || {},
                    //                     supportStaffDetails[
                    //                         `${el.locationState}`
                    //                     ],
                    //                     teacherAccountState
                    //                 )
                    //             supportStaffDocs[`${el.locationState}`] =
                    //                 accredDocs || {}
                    //             mandatoryRequiredCount += expiredCount
                    //         }
                    //     }

                    // Get submitted accreditation documents
                    // if (el.type === 'earlyChildcare') {
                    //     if (educatorDocs === null) { educatorDocs = {} }
                    //     const accredDocs = getAccreditationDocuments(teacherData.accreditation || {}, educatorDetails[`${el.locationState}`], teacherFields.accountState?.value, e => { mandatoryRequiredCount = mandatoryRequiredCount + e }, isPostReg);
                    //     educatorDocs[`${el.locationState}`] = accredDocs;
                    // }
                    // else if (el.type === 'school') {
                    //     if (schoolsDocs === null) { schoolsDocs = {} }
                    //     const accredDocs = getAccreditationDocuments(teacherData.accreditation || {}, schoolsDetails[`${el.locationState}`], teacherFields.accountState?.value, e => { mandatoryRequiredCount = mandatoryRequiredCount + e }, isPostReg);
                    //     schoolsDocs[`${el.locationState}`] = accredDocs;
                    // }
                    // }

                    // Upsert User Profiles
                    // if (
                    //     empSetting.type === 'school' ||
                    //     empSetting.type === 'both'
                    // ) {
                    //     dispatch(
                    //         setTeacherProfile({
                    //             profile: teacherData.profile,
                    //             favouriteSubjects:
                    //                 teacherData.favouriteSubjects,
                    //             years: teacherData.years,
                    //             type: teacherData.type,
                    //         })
                    //     )
                    // }
                    // if (empSetting.type === 'supportStaff') {
                    //     dispatch(
                    //         setSupportStaffProfile({
                    //             profile: teacherData.profile,
                    //             years: teacherData.years,
                    //             type: teacherData.type,
                    //             supportStaffProfile:
                    //                 teacherData.supportStaffProfile,
                    //         })
                    //     )
                    // }

                    // if (
                    //     empSetting.type === 'both' ||
                    //     empSetting.type === 'earlyChildcare'
                    // ) {
                    //     dispatch(
                    //         setEducatorProfile({
                    //             educatorProfile: teacherData.educatorProfile,
                    //             approachToLearning:
                    //                 teacherData.approachToLearning,
                    //             languages: teacherData.languages,
                    //             personalInterests:
                    //                 teacherData.personalInterests,
                    //         })
                    //     )
                    // }

                    // dispatch(setStateECCTeachingDocument(educatorDetails))
                    // dispatch(setStateTeachingDocument(schoolsDetails))
                    // dispatch(
                    //     setStateSupportStaffTeachingDocument(
                    //         supportStaffDetails
                    //     )
                    // )
                    // dispatch(setExpiredDocsCount(mandatoryRequiredCount))

                    // teacherFields.accreditation.earlyChildcare = educatorDocs
                    // teacherFields.accreditation.school = schoolsDocs
                    // teacherFields.accreditation.supportStaff = supportStaffDocs

                    // Get the state of the accreditation documents for locationState
                    // const accredState = getCombinedState([
                    //     ...(['school', 'both'].includes(empSetting.type) ?
                    //         [teacherFields.accreditation.school?.[`${userLocation}`]?.state as FieldState]
                    //         : []
                    //     ),
                    //     ...(['earlyChildcare', 'both'].includes(empSetting.type) ?
                    //         [teacherFields.accreditation.earlyChildcare?.[`${userLocation}`]?.state as FieldState]
                    //         : []
                    //     ),
                    // ]);

                    // Retrieve Educator Profile data
                    // if (
                    //     ['earlyChildcare', 'both'].includes(
                    //         empSetting.type as TeacherEmploymentSetting
                    //     )
                    // ) {
                    //     dispatch(setEducatorProfile({}))
                    // }

                    // if (empSetting.type === 'supportStaff') {
                    //     dispatch(
                    //         setSupportStaffProfile({
                    //             years: teacherData.years,
                    //             type: teacherData.type,
                    //             ...teacherData.profile,
                    //             ...teacherData.supportStaffProfile,
                    //         })
                    //     )
                    // }

                    // Retrieve Teacher Notifications Settings data
                    let teacherNotifications: TeacherNotifications = {}
                    if (teacherData.notificationSettings) {
                        teacherNotifications = {
                            bookingConfirmations:
                                teacherData.notificationSettings
                                    .bookingConfirmations !== undefined
                                    ? teacherData.notificationSettings
                                          .bookingConfirmations
                                    : true,
                            cancellationNotifications:
                                teacherData.notificationSettings
                                    .cancellationNotifications !== undefined
                                    ? teacherData.notificationSettings
                                          .cancellationNotifications
                                    : true,
                            completedBookings:
                                teacherData.notificationSettings
                                    .completedBookings !== undefined
                                    ? teacherData.notificationSettings
                                          .completedBookings
                                    : true,
                            onboardingEmails:
                                teacherData.notificationSettings
                                    .onboardingEmails !== undefined
                                    ? teacherData.notificationSettings
                                          .onboardingEmails
                                    : true,
                            sparkNews:
                                teacherData.notificationSettings.sparkNews !==
                                undefined
                                    ? teacherData.notificationSettings.sparkNews
                                    : true,
                            teacherUpdates:
                                teacherData.notificationSettings
                                    .teacherUpdates !== undefined
                                    ? teacherData.notificationSettings
                                          .teacherUpdates
                                    : true,
                            accountReminderEmails:
                                teacherData.notificationSettings
                                    .accountReminderEmails !== undefined
                                    ? teacherData.notificationSettings
                                          .accountReminderEmails
                                    : true,
                        }
                    } else {
                        teacherNotifications = {
                            bookingConfirmations: true,
                            cancellationNotifications: true,
                            completedBookings: true,
                            onboardingEmails: true,
                            sparkNews: true,
                            teacherUpdates: true,
                            accountReminderEmails: true,
                        }
                    }

                    if (!isPostReg) {
                        teacherFields.bank = {}
                        for (let key of Object.keys(teacherData.bank || {})) {
                            switch (key) {
                                // New data structure
                                case 'details': {
                                    let bankDetails = teacherData.bank
                                        .details as {
                                        [id: string]: BankDetails
                                    }
                                    if (bankDetails) {
                                        let [bankId, theBank] = Object.entries(
                                            bankDetails
                                        ).sort(
                                            ([_a, a1], [_b, b1]) =>
                                                (b1.timeSubmitted || 0) -
                                                (a1.timeSubmitted || 0)
                                        )?.[0]
                                        teacherFields.bank = {
                                            id: bankId,
                                            ...theBank,
                                        }
                                    }
                                    break
                                }
                                // Old data structure
                                case 'value':
                                    teacherFields.bank.value =
                                        teacherData.bank.value
                                    break
                                case 'type':
                                    teacherFields.bank.type =
                                        teacherData.bank.type
                                    break
                            }
                        }
                        teacherFields.bank.state = teacherData.bank?.state
                            ? teacherData.bank?.state
                            : FieldState.INCOMPLETE

                        teacherFields.super = {
                            state: teacherData.super?.state || 'incomplete',
                            ...(teacherData.super || {}),
                        }

                        teacherFields.tax = {
                            state: teacherData.tax?.state || 'incomplete',
                            ...(teacherData.tax || {}),
                        }

                        // Retrieve and process teacher Identity documents
                        if (teacherData.identity !== undefined) {
                            Object.keys(teacherData.identity).forEach((key) => {
                                const document = teacherData.identity[key]
                                if (document === undefined) return
                                if (
                                    document.documentType ===
                                    'photoHolidingIdentification'
                                ) {
                                    teacherFields.holdingIdPhotoURL =
                                        toIdentificationDocument(document, key)
                                    teacherFields.holdingIdPhotoURL.state =
                                        teacherFields.accountState?.value !==
                                        TeacherAccountState.SUBMITTED
                                            ? document.state
                                            : FieldState.SUBMITTED
                                } else {
                                    // Identification requirements dependent on user's reported working rights.
                                    switch (teacherData.workingRights?.value) {
                                        case 'temporaryVisaHolder': {
                                            if (
                                                document.documentType ===
                                                'passport'
                                            ) {
                                                teacherFields.primaryIdentification =
                                                    toIdentificationDocument(
                                                        document,
                                                        key
                                                    )
                                                teacherFields.primaryIdentification.state =
                                                    teacherFields.accountState
                                                        ?.value !==
                                                    TeacherAccountState.SUBMITTED
                                                        ? document.state
                                                        : FieldState.SUBMITTED
                                            } else {
                                                teacherFields.secondaryIdentification =
                                                    toIdentificationDocument(
                                                        document,
                                                        key
                                                    )
                                                teacherFields.secondaryIdentification.state =
                                                    teacherFields.accountState
                                                        ?.value !==
                                                    TeacherAccountState.SUBMITTED
                                                        ? document.state
                                                        : FieldState.SUBMITTED
                                            }
                                            break
                                        }
                                        case 'unrestrictedCitizen':
                                        case 'unrestrictedPermanentResident':
                                        default: {
                                            if (
                                                !teacherFields.primaryIdentification &&
                                                (document.documentType ===
                                                    'passport' ||
                                                    document.documentType ===
                                                        'birthCertificate' ||
                                                    document.documentType ===
                                                        'australianCitizenshipCertificate')
                                            ) {
                                                teacherFields.primaryIdentification =
                                                    toIdentificationDocument(
                                                        document,
                                                        key
                                                    )
                                                teacherFields.primaryIdentification.state =
                                                    teacherFields.accountState
                                                        ?.value !==
                                                    TeacherAccountState.SUBMITTED
                                                        ? document.state
                                                        : FieldState.SUBMITTED
                                            } else {
                                                teacherFields.secondaryIdentification =
                                                    toIdentificationDocument(
                                                        document,
                                                        key
                                                    )
                                                teacherFields.secondaryIdentification.state =
                                                    teacherFields.accountState
                                                        ?.value !==
                                                    TeacherAccountState.SUBMITTED
                                                        ? document.state
                                                        : FieldState.SUBMITTED
                                            }
                                        }
                                    }
                                }
                            })
                        }
                        // If any are missing, set as incomplete
                        if (!teacherFields.holdingIdPhotoURL) {
                            teacherFields.holdingIdPhotoURL = {
                                state: 'incomplete',
                            }
                        }
                        if (!teacherFields.primaryIdentification) {
                            teacherFields.primaryIdentification = {
                                state: 'incomplete',
                            }
                        }
                        if (!teacherFields.secondaryIdentification) {
                            teacherFields.secondaryIdentification = {
                                state: 'incomplete',
                            }
                        }
                    }

                    // Statement of Service (NSW only)
                    if (
                        (teacherData.setLocation?.homeState ||
                            teacherData.homeLocation?.homeState) === 'NSW' &&
                        teacherFields.accountState?.value &&
                        ![
                            TeacherAccountState.APPROVED,
                            TeacherAccountState.ONHOLD,
                        ].includes(
                            teacherFields.accountState
                                .value as TeacherAccountState
                        )
                    ) {
                        // if(teacherData.service) {
                        let services = teacherData.service as {
                            [id: string]: any
                        }
                        if (services) {
                            let [serviceId, theService] = Object.entries(
                                services
                            ).sort(
                                ([_a, a1], [_b, b1]) =>
                                    (b1.timeUploaded || 0) -
                                    (a1.timeUploaded || 0)
                            )?.[0]
                            teacherFields.service = {
                                serviceId: serviceId,
                                ...theService,
                            }
                        } else {
                            teacherFields.service = {}
                        }
                    }

                    dispatch(setRegFields(teacherFields))
                    dispatch(setDataLoaded(true))

                    dispatch(setTeacherNotifications(teacherNotifications))
                }
            })
        }

        return () => {
            if (unsub !== undefined) {
                logger.debug('DETACH-LISTENER: Teacher Data')
                unsub()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId])

    return null
})

const CrtListeners = memo(() => {
    const userId = useSelector(selectUserUid)
    const setting = useSelector(selectDerivedIncompleteTeacherEmploymentSetting)
    const isCrt = setting === 'school' || setting === 'both'
    const dispatch = useDispatch()

    // Profile listener
    useEffect(() => {
        if (!userId || !isCrt) return

        dispatch(setLoading('profile'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/profile`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(setData({ key: 'profile', value: snap.val() }))
                } else {
                    dispatch(setData({ key: 'profile', value: null }))
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setData({ key: 'profile', value: undefined }))
        }
    }, [isCrt, userId, dispatch])
    return null
})

const SupportStaffListeners = memo(() => {
    const userId = useSelector(selectUserUid)
    const setting = useSelector(selectDerivedIncompleteTeacherEmploymentSetting)
    const isSupportStaff = setting === 'supportStaff'
    const dispatch = useDispatch()

    // Support Staff Profile listener
    useEffect(() => {
        if (!userId || !isSupportStaff) return

        dispatch(setLoading('supportStaffProfile'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/supportStaffProfile`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(
                        setData({
                            key: 'supportStaffProfile',
                            value: snap.val(),
                        })
                    )
                } else {
                    dispatch(
                        setData({ key: 'supportStaffProfile', value: null })
                    )
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setData({ key: 'supportStaffProfile', value: undefined }))
        }
    }, [isSupportStaff, userId, dispatch])
    return null
})

const EducatorListeners = memo(() => {
    const userId = useSelector(selectUserUid)
    const setting = useSelector(selectDerivedIncompleteTeacherEmploymentSetting)
    const isEducator = setting === 'earlyChildcare' || setting === 'both'
    const dispatch = useDispatch()

    // Support Staff Profile listener
    useEffect(() => {
        if (!userId || !isEducator) return

        dispatch(setLoading('educatorProfile'))

        const unsub: Unsubscribe = onValue(
            ref(db, `teachers/${userId}/educatorProfile`),
            (snap) => {
                if (snap.exists()) {
                    dispatch(
                        setData({
                            key: 'educatorProfile',
                            value: snap.val(),
                        })
                    )
                } else {
                    dispatch(setData({ key: 'educatorProfile', value: null }))
                }
            }
        )

        // Cleanup
        return () => {
            unsub()
            dispatch(setData({ key: 'educatorProfile', value: undefined }))
        }
    }, [isEducator, userId, dispatch])
    return null
})

export const extractAccreditationDoc = (
    teacherData: any,
    accountState?: TeacherAccountState
) => {
    let accreditation: { [name: string]: AccreditationDocument } = {}
    Object.keys(teacherData.accreditation).forEach((key) => {
        const doc: AccreditationDocument = teacherData.accreditation[key]
        if (doc === undefined) return

        let isExpired = moment
            .tz(moment(), 'Australia/Melbourne')
            .isAfter(
                moment.tz(
                    moment(doc.expiryDate, 'DD-MM-YYYY'),
                    'Australia/Melbourne'
                )
            )

        // If there are multiple of the same type (e.g. update request is made)
        if (
            doc.accreditationType &&
            Object.keys(accreditation).includes(doc.accreditationType)
        ) {
            const tempDoc = {
                ...toAccreditationDocument(doc, key),
                ...(accountState === TeacherAccountState.SUBMITTED
                    ? { state: FieldState.SUBMITTED }
                    : isExpired
                      ? { state: FieldState.EXPIRED }
                      : null),
            }
            // Take more recent document
            if (
                moment(tempDoc.dateSubmitted, 'DD-MM-YYYY').isAfter(
                    moment(
                        accreditation[`${doc.accreditationType}`].dateSubmitted,
                        'DD-MM-YYYY'
                    )
                )
            ) {
                accreditation[`${doc.accreditationType}`] = tempDoc
            } else if (
                moment(tempDoc.dateSubmitted, 'DD-MM-YYYY').isSame(
                    moment(
                        accreditation[`${doc.accreditationType}`].dateSubmitted,
                        'DD-MM-YYYY'
                    )
                )
            ) {
                if (
                    tempDoc.expiryDate &&
                    accreditation[`${doc.accreditationType}`].expiryDate
                ) {
                    if (
                        moment(tempDoc.expiryDate, 'DD-MM-YYYY').isAfter(
                            moment(
                                accreditation[`${doc.accreditationType}`]
                                    .expiryDate,
                                'DD-MM-YYYY'
                            )
                        )
                    ) {
                        accreditation[`${doc.accreditationType}`] = tempDoc
                    }
                }
            }
        } else {
            accreditation[`${doc.accreditationType}`] = {
                ...toAccreditationDocument(doc, key),
                ...(accountState === TeacherAccountState.SUBMITTED
                    ? { state: FieldState.SUBMITTED }
                    : isExpired
                      ? { state: FieldState.EXPIRED }
                      : null),
            }
        }
    })
    return accreditation
}

export default TeacherDashboard
